
import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Building2, Hammer, FileText, Settings, Workflow, CalendarDays, Contact, Maximize, Minimize, Mail, Users, MapPinned, Network } from "lucide-react";
import Logo from "@/components/Logo";
import { supabase } from "@/integrations/supabase/client";
import MobileMenu from "./header/MobileMenu";
import { UserMenu } from "./header/UserMenu";
import { Button } from "./ui/button";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuthSession } from "@/hooks/useAuthSession";
import { FollowChatGPTControls } from "@/components/FollowChatGPT";

const Header = () => {
  const [username, setUsername] = useState<string>("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const { isAdmin } = useUserRole(userId);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const location = useLocation();
  const { session, isReady } = useAuthSession();

  // Traditional browser fullscreen toggle.
  useEffect(() => {
    document.documentElement.classList.remove('salesby-fill-viewport');
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    onChange();
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch (e) {
      console.error('Fullscreen toggle failed', e);
    }
  }, []);


  useEffect(() => {
    let mounted = true;

    const getProfile = async () => {
      try {
        if (!mounted) return;
        if (session?.user?.id) {
          setIsLoggedIn(true);
          setUserId(session.user.id);
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('username')
            .eq('id', session.user.id)
            .maybeSingle();
          
          if (error) {
            console.error('Error fetching profile:', error);
            toast({
              title: "Error",
              description: "Failed to load user profile",
              variant: "destructive",
            });
            return;
          }
          
          if (profile) {
            setUsername(profile.username);
          } else {
            // Set default values if no profile exists
            setUsername(session.user.email?.split('@')[0] || 'User');
            console.log('No profile found for user:', session.user.id);
          }
        } else {
          setIsLoggedIn(false);
          setUsername('');
          setUserId(undefined);
        }
      } catch (error) {
        console.error('Error in getProfile:', error);
        toast({
          title: "Error",
          description: "Failed to load user session",
          variant: "destructive",
        });
      }
    };

    if (isReady) void getProfile();

    return () => {
      mounted = false;
    };
  }, [isReady, session, toast]);

  return (
    <nav className="bg-background/80 backdrop-blur-md shadow-sm px-4 py-3 sticky top-0 z-50 border-b border-border caret-transparent">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Logo />
        </div>
        
        <div className="flex items-center space-x-4">
          {isLoggedIn ? (
            <>
              <FollowChatGPTControls />
              <TooltipProvider>
                <div className="hidden md:flex items-center space-x-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/index" onClick={(e) => { if (location.pathname === "/index") e.preventDefault(); }}>
                        <Button 
                          variant={location.pathname === "/index" ? "default" : "ghost"} 
                          size="sm"
                          className="flex items-center"
                        >
                          <LayoutDashboard className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>Dashboard</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/stages">
                        <Button 
                          variant={location.pathname === "/stages" ? "default" : "ghost"} 
                          size="sm"
                          className="flex items-center"
                        >
                          <Workflow className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>Pipeline</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/weeks">
                        <Button 
                          variant={location.pathname === "/weeks" ? "default" : "ghost"} 
                          size="sm"
                          className="flex items-center"
                        >
                          <CalendarDays className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>Weekly Log</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/planning">
                        <Button 
                          variant={location.pathname === "/planning" ? "default" : "ghost"} 
                          size="sm"
                          className="flex items-center"
                        >
                          <Network className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>Planning</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/prospecting">
                        <Button 
                          variant={location.pathname === "/prospecting" ? "default" : "ghost"} 
                          size="sm"
                          className="flex items-center"
                        >
                          <Hammer className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>Prospecting</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/trade-shows">
                        <Button 
                          variant={location.pathname === "/trade-shows" ? "default" : "ghost"} 
                          size="sm"
                          className="flex items-center"
                        >
                          <Building2 className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>Trade Shows</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/contacts">
                        <Button 
                          variant={location.pathname === "/contacts" ? "default" : "ghost"} 
                          size="sm"
                          className="flex items-center"
                        >
                          <Contact className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>Contacts</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/mapping">
                        <Button 
                          variant={location.pathname === "/mapping" ? "default" : "ghost"} 
                          size="sm"
                          className="flex items-center"
                        >
                          <MapPinned className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>Mapping</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/reports">
                        <Button 
                          variant={location.pathname === "/reports" ? "default" : "ghost"} 
                          size="sm"
                          className="flex items-center"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>Reports</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/communicator">
                        <Button 
                          variant={location.pathname === "/communicator" ? "default" : "ghost"} 
                          size="sm"
                          className="flex items-center"
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>Communicator</TooltipContent>
                  </Tooltip>



                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex items-center"
                        onClick={toggleFullscreen}
                      >
                        {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{isFullscreen ? "Exit Fullscreen" : "Fullscreen"}</TooltipContent>
                  </Tooltip>

                  <UserMenu username={username} />
                </div>
              </TooltipProvider>
              <MobileMenu isLoggedIn={isLoggedIn} onToggleFullscreen={toggleFullscreen} isFullscreen={isFullscreen} />
            </>
          ) : (
            <>
              <div className="hidden md:flex items-center space-x-4">
                <a 
                  href="https://www.salesby.us/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="ghost" size="sm">Privacy Policy</Button>
                </a>
                <Link to="/login">
                  <Button variant="ghost">Login</Button>
                </Link>
                <Link to="/register">
                  <Button variant="ghost">Register</Button>
                </Link>
              </div>
              <MobileMenu isLoggedIn={isLoggedIn} />
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Header;
